import React, { useContext, useState, useCallback, useEffect, useMemo } from 'react';
import {
    createUseStyles,
    Input,
    Textarea,
    Button,
    Advice,
    SectionForm,
    Checkbox,
    Text,
    Spacer,
    useTheme,
} from '@Sage/hoi-poi-ui';
import { getTools } from 'lib/services/copilotAgents';
import CopilotSidebarTabHeader from '../../components/CopilotSidebarTabHeader';
import { CopilotContext } from '../../../Context';

import styles from './styles';
const useStyles = createUseStyles(styles, 'CopilotSidebarAgentsForm');

const CopilotSidebarAgentsForm = ({ agent, onCancel, onSave }) => {
    const theme = useTheme();
    const classes = useStyles();
    const { refreshAgents } = useContext(CopilotContext);
    const [isLoading, setIsLoading] = useState(false);
    const [form, setForm] = useState(
        agent || {
            name: '',
            description: '',
            tools: [],
        },
    );
    const [tools, setTools] = useState();

    const selectedTools = useMemo(() => {
        return form?.tools?.map((tool) => tool.name) || [];
    }, [form?.tools]);

    const onChange = useCallback(
        (key) => (value) => {
            setForm({ ...form, [key]: value });
        },
        [form],
    );

    const onInnerCancel = useCallback(() => {
        onCancel();
        setTimeout(() => {
            setForm({
                name: '',
                description: '',
            });
        });
    }, [onCancel]);

    const onInnerSave = useCallback(() => {
        setIsLoading(true);
        onSave(form)
            .then(() => {
                onInnerCancel();
            })
            .finally(() => {
                setIsLoading(false);
                refreshAgents();
            });
    }, [form, onInnerCancel, onSave, refreshAgents]);

    const onChangeTool = useCallback(
        (name) => {
            if (selectedTools.includes(name)) {
                setForm({ ...form, tools: form.tools.filter((tool) => tool.name !== name) });
            } else {
                setForm({
                    ...form,
                    tools: [...form.tools, tools.find((tool) => tool.name === name)],
                });
            }
        },
        [form, selectedTools, tools],
    );

    useEffect(() => {
        getTools()
            .then(({ data }) => {
                setTools(data);
            })
            .catch((error) => {
                console.error(error);
            });
    }, []);

    const toolsOptions = useMemo(() => {
        return tools?.map((tool) => (
            <div className={classes.tool} key={tool.name}>
                <Checkbox
                    className={classes.toolCheckbox}
                    checked={selectedTools.includes(tool.name)}
                    onChange={() => onChangeTool(tool.name)}
                    color="actionMinor"
                />
                <div className={classes.toolContent}>
                    <Text bold>{tool.label || tool.name}</Text>
                    <Text type="caption" color={theme.colors.textLight.secondary}>
                        {tool.description}
                    </Text>
                </div>
            </div>
        ));
    }, [
        classes.tool,
        classes.toolCheckbox,
        classes.toolContent,
        onChangeTool,
        selectedTools,
        theme.colors.textLight.secondary,
        tools,
    ]);

    return (
        <div className={classes.root}>
            <CopilotSidebarTabHeader
                title={agent?.id ? 'Edit agent' : 'Create agent'}
                onBack={onInnerCancel}
                sideComponent={
                    <Button
                        type="primary"
                        onClick={onInnerSave}
                        isLoading={isLoading}
                        isDisabled={!form.name || !form.description || !form.instructions}
                    >
                        Save
                    </Button>
                }
            />
            <Advice
                type="semanticInfo"
                title="✨ Let's bring your agent to life!"
                showIcon
                defaultCollapsed={false}
            >
                ✅ Be clear about the agent&apos;s role (e.g. Help users understand product
                features). <br />
                🗣️ Define the tone (e.g. friendly, professional, concise). <br />
                🧠 Mention any specific knowledge or context it should use. <br />
                🚫 Add limits (e.g. Don&apos;t give legal advice).
            </Advice>
            <SectionForm title="Information">
                <Input
                    label="Name"
                    placeholder="Write the name of the agent"
                    value={form.name}
                    onChange={onChange('name')}
                    isFullWidth
                    isRequired
                />
                <Textarea
                    label="Description"
                    placeholder="Write a description of the agent"
                    value={form.description}
                    onChange={onChange('description')}
                    minRows={2}
                    maxRows={4}
                    isResizable={false}
                    isFullWidth
                    isRequired
                />
                <Textarea
                    className={classes.instructions}
                    label="Instructions"
                    placeholder="Need inspiration? Try:

“You are a helpful assistant that guides users through the sales process in a friendly and proactive way. Always suggest the next best action and keep answers short and clear.”"
                    value={form.instructions}
                    onChange={onChange('instructions')}
                    minRows={10}
                    maxRows={40}
                    isFullWidth
                    isRequired
                />
            </SectionForm>
            {tools?.length > 0 && (
                <SectionForm title="Tools">
                    <Spacer y={3} />
                    <div className={classes.tools}>{toolsOptions}</div>
                </SectionForm>
            )}
        </div>
    );
};

export default CopilotSidebarAgentsForm;
